import React, { useState } from 'react';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableRow, TableCell } from '../ui/table';
import { Code, Brain, Cpu } from 'lucide-react';

const TurnDetailsTechnical = ({ turn }) => {
  const [expandedPanels, setExpandedPanels] = useState(['nodes']);
  const togglePanel = (panel) => setExpandedPanels(prev => prev.includes(panel) ? prev.filter(p => p !== panel) : [...prev, panel]);
  const formatJSON = (jsonString) => { try { const parsed = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString; return JSON.stringify(parsed, null, 2); } catch { return jsonString; } };

  return (
    <div className="p-4">
      <h3 className="text-lg font-bold mb-3 flex items-center gap-1.5"><Code className="h-5 w-5" />Technical Details</h3>

      {turn.rumination && (
        <div className="mb-3 border border-white/10 rounded-lg overflow-hidden">
          <button onClick={() => togglePanel('rumination')} className="w-full flex items-center justify-between p-3 bg-white/[0.02] hover:bg-surface-2 transition-colors">
            <div className="flex items-center gap-2"><Brain className="h-4 w-4 text-brand-blue" /><span className="font-bold text-sm">Rumination State</span>
              {turn.rumination.approved !== null && <Badge variant={turn.rumination.approved ? 'success' : 'secondary'}>{turn.rumination.approved ? 'Approved' : 'Not Approved'}</Badge>}
            </div>
            <span className="text-ink-dim text-xs">{expandedPanels.includes('rumination') ? '▲' : '▼'}</span>
          </button>
          {expandedPanels.includes('rumination') && (
            <div className="p-3"><Table><TableBody>
              <TableRow><TableCell><span className="font-bold text-xs">Count</span></TableCell><TableCell className="text-sm">{turn.rumination.count || 0}</TableCell></TableRow>
              <TableRow><TableCell><span className="font-bold text-xs">Confidence</span></TableCell><TableCell className="text-sm">{turn.rumination.confidence !== undefined ? `${(turn.rumination.confidence * 100).toFixed(1)}%` : 'N/A'}</TableCell></TableRow>
              {turn.rumination.context && <TableRow><TableCell><span className="font-bold text-xs">Context</span></TableCell><TableCell className="text-sm">{turn.rumination.context}</TableCell></TableRow>}
              {turn.rumination.gaps && <TableRow><TableCell><span className="font-bold text-xs">Gaps</span></TableCell><TableCell className="text-sm">{turn.rumination.gaps}</TableCell></TableRow>}
              {turn.rumination.reasoning && <TableRow><TableCell><span className="font-bold text-xs">Reasoning</span></TableCell><TableCell className="text-sm">{turn.rumination.reasoning}</TableCell></TableRow>}
              {turn.rumination.prior_sources && <TableRow><TableCell><span className="font-bold text-xs">Prior Sources</span></TableCell><TableCell className="text-sm">{turn.rumination.prior_sources}</TableCell></TableRow>}
            </TableBody></Table></div>
          )}
        </div>
      )}

      {turn.nodes && turn.nodes.length > 0 && (
        <div className="mb-3 border border-white/10 rounded-lg overflow-hidden">
          <button onClick={() => togglePanel('nodes')} className="w-full flex items-center justify-between p-3 bg-white/[0.02] hover:bg-surface-2 transition-colors">
            <div className="flex items-center gap-2"><Cpu className="h-4 w-4 text-primary-500" /><span className="font-bold text-sm">Node Executions</span><Badge variant="secondary">{turn.nodes.length} nodes</Badge></div>
            <span className="text-ink-dim text-xs">{expandedPanels.includes('nodes') ? '▲' : '▼'}</span>
          </button>
          {expandedPanels.includes('nodes') && (
            <div className="p-3">
              {turn.nodes.map((node, index) => (
                <div key={index} className={`pb-3 ${index < turn.nodes.length - 1 ? 'mb-3 border-b border-white/5' : ''}`}>
                  <p className="text-xs font-bold mb-1">Node #{index + 1}: {node.node}</p>
                  <div className="flex gap-1 flex-wrap mt-1">
                    <Badge variant="outline">Step: {node.step}</Badge>
                    {node.checkpoint_id && <Badge variant="outline">Checkpoint: {node.checkpoint_id}</Badge>}
                  </div>
                  {node.writes_json && (
                    <div className="mt-2">
                      <span className="text-[10px] font-bold text-ink-dim">Writes JSON:</span>
                      <pre className="mt-1 p-2 bg-black/30 rounded text-xs font-mono overflow-auto whitespace-pre-wrap">{formatJSON(node.writes_json)}</pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {turn.narrative_query && (
        <div className="mb-3 border border-white/10 rounded-lg overflow-hidden">
          <button onClick={() => togglePanel('narrative')} className="w-full flex items-center justify-between p-3 bg-white/[0.02] hover:bg-surface-2 transition-colors">
            <span className="font-bold text-sm">Narrative Query</span>
            <span className="text-ink-dim text-xs">{expandedPanels.includes('narrative') ? '▲' : '▼'}</span>
          </button>
          {expandedPanels.includes('narrative') && <div className="p-3 bg-black/20"><p className="text-sm">{turn.narrative_query}</p></div>}
        </div>
      )}

      {turn.classifications && turn.classifications.length > 0 && (
        <div className="border border-white/10 rounded-lg overflow-hidden">
          <button onClick={() => togglePanel('classifications')} className="w-full flex items-center justify-between p-3 bg-white/[0.02] hover:bg-surface-2 transition-colors">
            <span className="font-bold text-sm">Classifications</span>
            <span className="text-ink-dim text-xs">{expandedPanels.includes('classifications') ? '▲' : '▼'}</span>
          </button>
          {expandedPanels.includes('classifications') && (
            <div className="p-3">
              {turn.classifications.map((c, index) => (
                <div key={index} className={`pb-2 ${index < turn.classifications.length - 1 ? 'mb-2 border-b border-white/5' : ''}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-xs font-bold">{c.agent}</span>
                    {c.confidence !== undefined && <Badge variant={c.confidence > 0.7 ? 'success' : c.confidence > 0.4 ? 'warning' : 'destructive'}>{`${(c.confidence * 100).toFixed(0)}%`}</Badge>}
                  </div>
                  {c.reasoning && <p className="text-sm text-ink-dim">{c.reasoning}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TurnDetailsTechnical;
